import React, { useEffect, useState, useMemo } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import ProtectedRoute from "./components/ProtectedRoute";
import LoginPage from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import GuideListPage from "./pages/guides/GuideListPage";
import { AdminHeader } from "./components/AdminHeader";
import { AdminSidebar } from "./components/AdminSidebar";
import { signOut } from "./lib/mockAuth";
import SeekerListPage from "./pages/seekers/SeekerListPage";
import EventsListPage from "./pages/events/EventsListPage";
import AlertPlaceholderPage from "./pages/alerts/AlertPlaceholderPage";
import ResourcePlaceholderPage from "./pages/resources/ResourcePlaceholderPage";
import AdminUsersPage from "./pages/admins/AdminUsersPage";
import TermsBuilderPage from "./pages/terms/TermsBuilderPage";
import NotificationTemplatesPage from "./pages/notifications/NotificationTemplatesPage";
import { MessagesPage } from "./pages/messages/MessagesPage";
import { AdminFooter } from "./components/AdminFooter";
import { getSupabaseClient } from "./lib/supabase";
import { PostHogIdentify } from "./components/PostHogIdentify";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

export default function App() {
  const env = (window as any).__ENV__;
  if (!env) return <p>⚠️ App config not loaded</p>;

  return (
    <BrowserRouter>
      <ScrollToTop />
      <PostHogIdentify />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/forgot" element={<ForgotPassword />} />
        <Route path="/auth/reset" element={<ResetPassword />} />

        <Route element={<ProtectedRoute />}>
          <Route
            path="/admin"
            element={<Navigate to="/admin/guides" replace />}
          />
          <Route
            path="/admin/guides"
            element={
              <AdminShell>
                <GuideListPage />
              </AdminShell>
            }
          />
          <Route
            path="/admin/seekers"
            element={
              <AdminShell>
                <SeekerListPage />
              </AdminShell>
            }
          />
          <Route
            path="/admin/events"
            element={
              <AdminShell>
                <EventsListPage />
              </AdminShell>
            }
          />
          <Route
            path="/admin/alerts"
            element={
              <AdminShell>
                <AlertPlaceholderPage />
              </AdminShell>
            }
          />
          <Route
            path="/admin/resources"
            element={
              <AdminShell>
                <ResourcePlaceholderPage />
              </AdminShell>
            }
          />
          <Route
            path="/admin/users"
            element={
              <AdminShell>
                <AdminUsersPage />
              </AdminShell>
            }
          />
          <Route
            path="/admin/terms"
            element={
              <AdminShell>
                <TermsBuilderPage />
              </AdminShell>
            }
          />
          <Route
            path="/admin/notifications"
            element={
              <AdminShell>
                <NotificationTemplatesPage />
              </AdminShell>
            }
          />
          <Route
            path="/admin/messages"
            element={
              <AdminShell>
                <MessagesPage />
              </AdminShell>
            }
          />
        </Route>

        <Route path="*" element={<Navigate to="/admin/guides" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

type AdminShellProps = {
  children: React.ReactNode;
};

function AdminShell({ children }: AdminShellProps) {
  const navigate = useNavigate();
  const [supportUnreadCount, setSupportUnreadCount] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const supabase = useMemo(() => getSupabaseClient(), []);

  useEffect(() => {
    let cancelled = false;

    const fetchUnread = async () => {
      try {
        const { data, error } = await supabase
          .from("support_conversations")
          .select("admin_unread_count");

        if (!error && data && !cancelled) {
          const total = data.reduce(
            (sum: number, row: { admin_unread_count: number }) =>
              sum + (row.admin_unread_count || 0),
            0
          );
          setSupportUnreadCount(total);
        }
      } catch {
        // ignore
      }
    };

    fetchUnread();
    const intervalId = setInterval(fetchUnread, 15000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [supabase]);

  const handleChatClick = () => {
    navigate("/admin/messages");
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <AdminHeader
        onSignOut={() => {
          signOut();
          window.location.href = "/login";
        }}
        onMenuClick={() => setSidebarOpen(true)}
        onChatClick={handleChatClick}
        hasUnreadMessages={supportUnreadCount > 0}
        unreadCount={supportUnreadCount}
      />

      <div className="flex flex-1 min-h-0">
        <AdminSidebar
          mobileOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />

        <main className="flex-1 min-w-0 overflow-x-auto">{children}</main>
      </div>
      <AdminFooter />
    </div>
  );
}
